/* Moments Etched v15.4.7.42 — Required proof approval + inside-branding display labels */
(() => {
  const INTERNAL_NECK_PLACEMENT = 'Custom Neck Label';

  const getNeckMode = (root) => String(
    root?.dataset?.meNeckLabelMode ||
    root?.querySelector?.('[data-me-neck-label-choice]:checked')?.value ||
    'original'
  );

  const neckDisplayLabel = (root) => {
    const mode = getNeckMode(root);
    if (mode === 'me-branding') return 'Moments Etched Label';
    if (mode === 'custom') return 'Your Brand Label';
    return 'Neck Label';
  };

  const displayPlacement = (root, placement) => {
    return placement === INTERNAL_NECK_PLACEMENT ? neckDisplayLabel(root) : placement;
  };

  const relabelPreview = (root) => {
    if (!root) return;
    const label = neckDisplayLabel(root);

    /* Keep data-me-preview-placement and the core placement label INTERNAL.
       Only customer-facing text changes so calibration/preview routing remains stable. */
    root.querySelectorAll('.me-studio-preview-tabs [data-me-preview-placement="Custom Neck Label"]').forEach((button) => {
      if (button.textContent !== label) button.textContent = label;
      button.setAttribute('aria-label', `Preview ${label}`);
    });

    const corePlacement = String(root.querySelector('[data-me-preview-placement-label]')?.textContent || '').trim();
    if (corePlacement === INTERNAL_NECK_PLACEMENT) {
      root.querySelectorAll('[data-me-studio-preview-label], .me-designer__preview-toolbar h3, [data-me-designer-active-location]').forEach((node) => {
        if (node && !node.matches('[data-me-preview-placement-label]')) node.textContent = label;
      });
    }
  };

  const configureProofExperience = (root) => {
    const reviewPurchase = root.querySelector('[data-me-review-purchase]');
    const approval = root.querySelector('[data-me-sidebar-approval]');
    const proof = root.querySelector('[data-me-proof-checkbox]');
    if (!reviewPurchase || !approval || !proof) return;

    const banner = reviewPurchase.querySelector('.me-studio-proof-banner');
    if (banner) {
      banner.classList.add('me-stage5-proof-intro');
      banner.innerHTML = `
        <div class="me-stage5-proof-intro__topline">
          <span class="me-stage5-proof-intro__badge">Required</span>
          <strong>Digital proof approval</strong>
        </div>
        <span>Every custom apparel order receives a digital proof by email. Production begins only after your written approval.</span>
      `;
    }

    const heading = approval.querySelector('.me-sidebar-approval__heading');
    if (heading) {
      heading.innerHTML = `
        <p>Proof &amp; approval</p>
        <span>Your proof is included and written approval is required before printing.</span>
      `;
    }

    const option = proof.closest('.me-sidebar-approval__option');
    if (option) {
      option.classList.add('me-stage5-proof-option');
      const strong = option.querySelector('strong');
      const small = option.querySelector('small');
      if (strong) strong.textContent = 'Digital proof included with every custom order.';
      if (small) small.textContent = 'We’ll email your production mockup and wait for your written approval before printing. Approval timing may affect turnaround.';
    }

    let state = approval.querySelector('[data-me-stage5-proof-state]');
    if (!state) {
      state = document.createElement('div');
      state.className = 'me-stage5-proof-state';
      state.dataset.meStage5ProofState = '';
      state.setAttribute('role', 'status');
      state.setAttribute('aria-live', 'polite');
      option?.insertAdjacentElement('afterend', state);
    }

    let flow = approval.querySelector('[data-me-stage5-proof-flow]');
    if (!flow) {
      flow = document.createElement('div');
      flow.className = 'me-stage5-proof-flow';
      flow.dataset.meStage5ProofFlow = '';
      flow.innerHTML = `
        <span class="me-stage5-proof-flow__lead">Required process:</span>
        <span><b>1</b> Proof emailed</span>
        <span aria-hidden="true">→</span>
        <span><b>2</b> You approve by email</span>
        <span aria-hidden="true">→</span>
        <span><b>3</b> Production begins</span>
      `;
      state.insertAdjacentElement('afterend', flow);
    }

    const sync = ({ announce = false } = {}) => {
      if (!proof.checked) proof.checked = true;
      approval.classList.add('is-proof-requested');
      option?.classList.add('is-selected', 'is-required-proof');
      proof.setAttribute('aria-readonly', 'true');
      proof.setAttribute('aria-label', 'Digital proof required before production');
      proof.tabIndex = -1;

      if (state) {
        state.classList.add('is-requested');
        state.innerHTML = '<span aria-hidden="true">✓</span><div><strong>Approval required</strong><p>We’ll email your proof and wait for your written approval before printing.</p></div>';
        state.setAttribute('aria-live', announce ? 'polite' : 'off');
      }
    };

    if (!proof.dataset.meV154742RequiredProofBound) {
      proof.dataset.meV154742RequiredProofBound = 'true';
      const keepRequired = (event) => {
        event?.preventDefault?.();
        proof.checked = true;
        sync({ announce: false });
      };
      proof.addEventListener('click', keepRequired);
      proof.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Enter') keepRequired(event);
      });
      option?.addEventListener('click', (event) => {
        if (event.target.closest('a,button')) return;
        keepRequired(event);
      });
    }

    if (!proof.checked) {
      proof.checked = true;
      proof.dispatchEvent(new Event('change', { bubbles: true }));
    }
    sync({ announce: false });
  };

  const initRoot = (root) => {
    if (!root) return;
    configureProofExperience(root);
    relabelPreview(root);

    const tabs = root.querySelector('.me-studio-preview-tabs');
    if (tabs && !tabs.dataset.meV154711Observed) {
      tabs.dataset.meV154711Observed = 'true';
      new MutationObserver(() => relabelPreview(root)).observe(tabs, { childList: true, subtree: true });
    }

    if (!root.dataset.meV154711StageObserved) {
      root.dataset.meV154711StageObserved = 'true';
      new MutationObserver((mutations) => {
        if (mutations.some((mutation) => ['data-me-studio-stage','data-me-neck-label-mode'].includes(mutation.attributeName))) {
          requestAnimationFrame(() => {
            configureProofExperience(root);
            relabelPreview(root);
          });
        }
      }).observe(root, { attributes: true, attributeFilter: ['data-me-studio-stage', 'data-me-neck-label-mode'] });
    }
  };

  const init = () => document.querySelectorAll('[data-me-designer]').forEach(initRoot);

  window.addEventListener('me:neck-label-updated', () => requestAnimationFrame(init));
  window.addEventListener('me:placements-updated', () => requestAnimationFrame(init));
  window.addEventListener('me:preview-placement', (event) => {
    const placement = event.detail?.placement || event.detail?.placementName || '';
    if (!placement) return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      document.querySelectorAll('[data-me-designer]').forEach((root) => {
        if (placement === INTERNAL_NECK_PLACEMENT) {
          const shown = displayPlacement(root, placement);
          root.querySelectorAll('[data-me-studio-preview-label], .me-designer__preview-toolbar h3, [data-me-designer-active-location]').forEach((node) => {
            if (node && !node.matches('[data-me-preview-placement-label]')) node.textContent = shown;
          });
        }
        relabelPreview(root);
      });
    }));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
